import {Component, OnInit, ViewChild} from '@angular/core';
import {Keys, StorageService} from '../../../../service/storage.service';
import {Loan} from '../../../../model/loan.model';
import {LoanDetailsService} from '../../../../service/loan-details.service';
import {MatDialog, MatTableDataSource} from '@angular/material';
import {ManualinvestdetailComponent} from '../../../../portfolio-creation/manualinvestdetail/manualinvestdetail.component';
import {PortfolioService} from '../../../../service/portfolio.service';
import {ConfirmPopUpComponent} from '../../../../comfirm-pop-up/confirm-pop-up.component';
import moment = require('moment-timezone');
import {NotFoundError} from '../../../../errors/NotFoundError';
import {Toaster, Type} from '../../../../toaster/Toaster';
import {ConfigService} from '../../../../service/config.service';
import {EntityGoneError} from '../../../../errors/EntityGoneError';
import {PreferencesService} from '../../../../service/preferences.service';
import {Router} from '@angular/router';
import {MatPaginator} from '@angular/material/paginator';
import { animate, state, style, transition, trigger } from '@angular/animations';


interface LenderAllocatedLoan {
  loanId: string;
  loanApplicationId: string;
  profile: string;
  portfolioId: string;
  rating: string;
  tenure: number;
  loan_amount: number;
  filled_percentage: number;
  loan_reason: string;
  investmentInLoan: number;
  reversalExpiryTime: Date;
  isReversalExpired: boolean;
  roi: number;
  debt_info: any;
}

@Component({
  templateUrl: './investor-allocated-loans.component.html',
  styleUrls: ['../../../../../assets/styles/table-styles.scss', './investor-allocated-loans.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', minHeight: "*" })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],

})
export class InvestorAllocatedLoansComponent implements OnInit {

  private readonly router: Router;
  private storageService: StorageService;
  private matDialog: MatDialog;
  private configService: ConfigService;
  private loanDetailsService: LoanDetailsService;
  private portfolioService: PortfolioService;
  private preferencesService: PreferencesService;

  investorName: string;
  lenderAllocatedLoanIdToBeDeleted: string;
  displayedColumns = [
    'borrowerDetails', 'rating', 'tenure', 'loanAmount', 'filledPercentage', 'loanPurpose', 'investmentInLoan', 'roi', 'reversalExpiryTime', 'reversal'
  ];
  dataSource = new MatTableDataSource<LenderAllocatedLoan>();
  allocatedLoanNotification: boolean;
  networkCallInProgress: boolean;
  expandedElement: LenderAllocatedLoan | null;
  public isMobile: boolean = false;
  screenWidth: number;

  @ViewChild(MatPaginator, {static: false})
  set paginator(value: MatPaginator) {
    if (this.dataSource) {
      this.dataSource.paginator = value;
    }
  }

  constructor(router: Router, storageService: StorageService, matDialog: MatDialog, configService: ConfigService,
              loanDetailsService: LoanDetailsService, portfolioService: PortfolioService, preferencesService: PreferencesService) {
    this.router = router;
    this.storageService = storageService;
    this.matDialog = matDialog;
    this.configService = configService;
    this.loanDetailsService = loanDetailsService;
    this.portfolioService = portfolioService;
    this.preferencesService = preferencesService;
  }
  ngAfterViewInit() {
    this.screenWidth = window.innerWidth;
    this.isMobile = (<any>window).innerWidth < 500;
    if (this.isMobile) {
      this.displayedColumns = ['borrowerDetails', 'rating', 'tenure','accordion_arrow'];
    }
  }


  async ngOnInit(): Promise<void> {

    this.networkCallInProgress = true;
    this.investorName = this.storageService.getItem(Keys.NAME);
    const lenderId = this.storageService.getItem(Keys.LENDER_ID);
    const lenderProfileId = this.storageService.getItem(Keys.PROFILE_ID);
    const preferences = await this.preferencesService.getUserPreferences(lenderProfileId);
    this.allocatedLoanNotification = preferences && preferences.allocatedLoanNotification;

    const allocatedLoans = await this.loanDetailsService.getAllocatedLoansFor(lenderId);
    this.dataSource.data = allocatedLoans ? this.populateAllocatedLoansData(lenderId, allocatedLoans) : undefined;
    this.networkCallInProgress = false;
  }

  viewBorrowerDetails(loan) {
    this.matDialog.open(ManualinvestdetailComponent, {data: loan, panelClass: 'manual-invest-details-dialog'});
  }

  async reverseInvestment(loan: LenderAllocatedLoan): Promise<void> {
    this.matDialog.open(ConfirmPopUpComponent, {
      data: {
        title: 'Confirm Investment Reversal',
        message: 'This action is irreversible. Are you sure you want to reverse the investment?',
        positiveButtonText: 'Reverse',
        negativeButtonText: 'Cancel'
      },
    }).afterClosed().subscribe(async result => {
      if (result) {
        this.lenderAllocatedLoanIdToBeDeleted = loan.loanId;
        try {
          await this.portfolioService.reverseInvestment(loan.portfolioId, loan.loanId);
          Toaster.toast(`Investment reversed`, Type.Success);
          this.dataSource.data = this.dataSource.data.filter(allocatedLoan => allocatedLoan.loanId !== loan.loanId);
          if (this.dataSource.data.length === 0) {
            await this.router.navigate(['investordashboard']);
            return;
          }
        } catch (error) {
          if (error instanceof EntityGoneError) {
            Toaster.toast(`Loan can no longer be reversed as it has passed the reversal expiry threshold`, Type.Error);
            const lenderAllocationData = this.dataSource.data.find(allocatedLoan => allocatedLoan.loanId === loan.loanId);
            lenderAllocationData.isReversalExpired = true;
          } else if (error.status === NotFoundError.statusCode) {
            Toaster.toast(`You no longer have any investment in the selected loan`, Type.Error);
            this.dataSource.data = this.dataSource.data.filter(allocatedLoan => allocatedLoan.loanId !== loan.loanId);
          } else {
            Toaster.toast(`Something went wrong. Please try again later.`, Type.Error);
            throw error;
          }
        } finally {
          this.lenderAllocatedLoanIdToBeDeleted = undefined;
        }
      }
    });
  }

  private populateAllocatedLoansData(lenderId: string, allocatedLoans: Loan[]): LenderAllocatedLoan[] {
    const noOfHoursForReversalExpiration = this.configService.getNoOfHoursForReversalExpiration();
    return allocatedLoans.map(loan => {
      const lenderInvested = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
      const reversalExpiryTime = moment(lenderInvested.timestamp).tz('Asia/Kolkata').add(noOfHoursForReversalExpiration, 'hours');
      return {
        loanId: loan._id,
        loanApplicationId: loan.loanApplicationId,
        profile: loan.profile,
        portfolioId: lenderInvested.portfolio_id,
        rating: loan.rating,
        tenure: loan.loan_tenure,
        loan_amount: loan.loan_amount,
        filled_percentage: loan.filled_percentage,
        loan_reason: loan.loan_reason,
        investmentInLoan: lenderInvested.invested_amount,
        reversalExpiryTime: reversalExpiryTime.format('Do MMM YYYY, h:mm A'),
        isReversalExpired: moment().isAfter(reversalExpiryTime),
        roi: loan.roi,
        debt_info: loan.debt_info
      };
    });
  }
}
